import React, { useState, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { supabase } from '../supabaseClient';

const Contact = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    companyName: '',
    serviceInterest: '',
    monthlyVolume: '',
    message: '',
    agreeToSMS: false,
    agreeToTCPA: false
  });
  const [currentStep, setCurrentStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');
  const messageRef = useRef(null);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitMessage('');

    try {
      const { error } = await supabase
        .from('rmq_digital_leads')
        .insert([{
          full_name: formData.fullName,
          email: formData.email,
          phone: formData.phone,
          company_name: formData.companyName,
          service_interest: formData.serviceInterest,
          monthly_volume: formData.monthlyVolume,
          message: formData.message,
          agree_to_sms: formData.agreeToSMS,
          agree_to_tcpa: formData.agreeToTCPA,
          status: 'new',
          source: 'rmq_digital',
          created_at: new Date().toISOString()
        }]);

      if (error) throw error;

      setSubmitMessage('✓ Thank you! We will contact you within 24 hours to discuss your marketing needs.');
      setCurrentStep(1);
      setFormData({
        fullName: '',
        email: '',
        phone: '',
        companyName: '',
        serviceInterest: '',
        monthlyVolume: '',
        message: '',
        agreeToSMS: false,
        agreeToTCPA: false
      });
      
      // Scroll to message
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } catch (error) {
      setSubmitMessage('❌ There was an error submitting your request. Please try again or contact support@rmqdigital.com');
      console.error('Supabase Error:', error);
      
      // Scroll to error message
      setTimeout(() => {
        if (messageRef.current) {
          messageRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }, 100);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div style={{background: '#f9fafb', minHeight: '100vh', paddingTop: '100px'}}>
      <Helmet>
        <title>Contact Us - Get Started with Digital Marketing | RMQ Digital</title>
        <meta name="description" content="Contact RMQ Digital for professional SMS and email marketing services. TCPA compliant campaigns with express written consent." />
        <meta name="keywords" content="contact, digital marketing inquiry, SMS marketing quote, email campaign services" />
      </Helmet>

      <section style={{background: 'linear-gradient(135deg, #6d28d9 0%, #8b5cf6 100%)', padding: '4rem 2rem', color: 'white'}}>
        <div style={{maxWidth: '1200px', margin: '0 auto'}}>
          <div style={{maxWidth: '700px', margin: '0 auto', textAlign: 'center'}}>
            <h1 style={{fontSize: '3rem', fontWeight: '800', marginBottom: '1rem', fontFamily: 'Space Grotesk'}}>
              Let's Grow Your Business
            </h1>
            <p style={{fontSize: '1.25rem', color: 'rgba(255,255,255,0.9)'}}>
              Tell us about your marketing needs and we'll create a custom solution for you
            </p>
          </div>
        </div>
      </section>

      <section style={{padding: '4rem 2rem'}}>
        <div style={{maxWidth: '900px', margin: '0 auto'}}>
          
          {/* Progress Bar */}
          <div style={{marginBottom: '3rem'}}>
            <div style={{display: 'flex', justifyContent: 'space-between', marginBottom: '1rem'}}>
              {['Contact Info', 'Service Details'].map((step, index) => (
                <div key={index} style={{flex: 1, textAlign: 'center'}}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    background: currentStep > index ? 'linear-gradient(135deg, #6d28d9, #8b5cf6)' : '#e5e7eb',
                    color: currentStep > index ? 'white' : '#9ca3af',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '0 auto',
                    fontWeight: '700',
                    fontSize: '1.125rem'
                  }}>
                    {index + 1}
                  </div>
                  <div style={{fontSize: '0.875rem', marginTop: '0.5rem', color: currentStep > index ? '#6d28d9' : '#9ca3af', fontWeight: '600'}}>
                    {step}
                  </div>
                </div>
              ))}
            </div>
            <div style={{height: '4px', background: '#e5e7eb', borderRadius: '2px', position: 'relative'}}>
              <div style={{
                height: '100%',
                background: 'linear-gradient(90deg, #6d28d9, #8b5cf6)',
                borderRadius: '2px',
                width: `${(currentStep / 2) * 100}%`,
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>

          {submitMessage && (
            <div 
              ref={messageRef}
              style={{
                background: submitMessage.includes('❌') ? '#fef2f2' : '#d1fae5', 
                padding: '1.5rem', 
                borderRadius: '12px', 
                marginBottom: '2rem', 
                border: `2px solid ${submitMessage.includes('❌') ? '#ef4444' : '#10b981'}`, 
                color: submitMessage.includes('❌') ? '#991b1b' : '#065f46',
                fontSize: '1.1rem',
                fontWeight: '500',
                textAlign: 'center',
                boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
              }}
            >
              {submitMessage}
            </div>
          )}

          <div style={{background: 'white', borderRadius: '16px', padding: '3rem', boxShadow: '0 4px 20px rgba(0,0,0,0.08)'}}>
            <form onSubmit={handleSubmit}>
              
              {/* Step 1: Contact Info */}
              {currentStep === 1 && (
                <div>
                  <h2 style={{fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: '#1f2937'}}>
                    Your Contact Information
                  </h2>
                  
                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                      Full Name *
                    </label>
                    <input
                      type="text"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      placeholder="John Doe"
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        transition: 'all 0.3s ease'
                      }}
                    />
                  </div>

                  <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem'}}>
                    <div>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                        Email Address *
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="john@company.com"
                        style={{
                          width: '100%',
                          padding: '0.875rem 1rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>

                    <div>
                      <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                        Phone Number *
                      </label>
                      <input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="(555) 123-4567"
                        style={{
                          width: '100%',
                          padding: '0.875rem 1rem',
                          border: '2px solid #e5e7eb',
                          borderRadius: '10px',
                          fontSize: '1rem'
                        }}
                      />
                    </div>
                  </div>

                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                      Company Name
                    </label>
                    <input
                      type="text"
                      name="companyName"
                      value={formData.companyName}
                      onChange={handleChange}
                      placeholder="Your Company LLC"
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '1rem'
                      }}
                    />
                  </div>

                  {/* TCPA Consent - Required at time of phone collection */}
                  <div style={{background: '#ede9fe', padding: '1.5rem', borderRadius: '12px', marginBottom: '1.5rem', border: '2px solid #8b5cf6'}}>
                    <h3 style={{fontSize: '1rem', fontWeight: '700', marginBottom: '1rem', color: '#6d28d9'}}>
                      Required Consent
                    </h3>
                    
                    <div style={{marginBottom: '1rem'}}>
                      <label style={{display: 'flex', alignItems: 'start', gap: '0.75rem', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          name="agreeToSMS"
                          checked={formData.agreeToSMS}
                          onChange={handleChange}
                          required
                          style={{marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0}}
                        />
                        <span style={{fontSize: '0.9rem', lineHeight: '1.6', color: '#6d28d9'}}>
                          <strong>SMS Consent:</strong> I consent to receive text messages from RMQ Digital at the phone number provided. Message and data rates may apply. Reply STOP to opt out. <strong>Consent is not required as a condition of purchasing any goods or services.</strong>
                        </span>
                      </label>
                    </div>

                    <div>
                      <label style={{display: 'flex', alignItems: 'start', gap: '0.75rem', cursor: 'pointer'}}>
                        <input
                          type="checkbox"
                          name="agreeToTCPA"
                          checked={formData.agreeToTCPA}
                          onChange={handleChange}
                          required
                          style={{marginTop: '0.25rem', width: '18px', height: '18px', cursor: 'pointer', flexShrink: 0}}
                        />
                        <span style={{fontSize: '0.9rem', lineHeight: '1.6', color: '#6d28d9'}}>
                          <strong>TCPA Consent:</strong> I authorize RMQ Digital to contact me via phone (including autodialed/pre-recorded calls), email, and SMS at the contact information provided, even if on a Do Not Call list.
                        </span>
                      </label>
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={() => setCurrentStep(2)}
                    disabled={!formData.agreeToSMS || !formData.agreeToTCPA}
                    style={{
                      width: '100%',
                      padding: '1rem',
                      background: (!formData.agreeToSMS || !formData.agreeToTCPA) ? '#9ca3af' : 'linear-gradient(135deg, #6d28d9, #8b5cf6)',
                      color: 'white',
                      border: 'none',
                      borderRadius: '10px',
                      fontSize: '1.125rem',
                      fontWeight: '700',
                      cursor: (!formData.agreeToSMS || !formData.agreeToTCPA) ? 'not-allowed' : 'pointer',
                      transition: 'all 0.3s ease',
                      opacity: (!formData.agreeToSMS || !formData.agreeToTCPA) ? 0.6 : 1
                    }}
                  >
                    Continue →
                  </button>
                </div>
              )}

              {/* Step 2: Service Details */}
              {currentStep === 2 && (
                <div>
                  <h2 style={{fontSize: '1.75rem', fontWeight: '700', marginBottom: '2rem', color: '#1f2937'}}>
                    Tell Us About Your Needs
                  </h2>

                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                      Service Interest
                    </label>
                    <select
                      name="serviceInterest"
                      value={formData.serviceInterest}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="">Select a service</option>
                      <option value="sms-marketing">SMS Marketing</option>
                      <option value="email-marketing">Email Marketing</option>
                      <option value="both">SMS & Email Marketing</option>
                      <option value="campaign-management">Campaign Management</option>
                      <option value="automation">Marketing Automation</option>
                      <option value="reseller">Reseller Services</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                      Expected Monthly Volume
                    </label>
                    <select
                      name="monthlyVolume"
                      value={formData.monthlyVolume}
                      onChange={handleChange}
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '1rem'
                      }}
                    >
                      <option value="">Select volume</option>
                      <option value="under-10k">Under 10,000 messages</option>
                      <option value="10k-50k">10,000 - 50,000 messages</option>
                      <option value="50k-100k">50,000 - 100,000 messages</option>
                      <option value="100k-500k">100,000 - 500,000 messages</option>
                      <option value="over-500k">Over 500,000 messages</option>
                    </select>
                  </div>

                  <div style={{marginBottom: '1.5rem'}}>
                    <label style={{display: 'block', marginBottom: '0.5rem', fontWeight: '600', color: '#374151'}}>
                      Additional Information
                    </label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="4"
                      placeholder="Tell us more about your marketing goals..."
                      style={{
                        width: '100%',
                        padding: '0.875rem 1rem',
                        border: '2px solid #e5e7eb',
                        borderRadius: '10px',
                        fontSize: '1rem',
                        fontFamily: 'inherit'
                      }}
                    ></textarea>
                  </div>

                  <div style={{display: 'flex', gap: '1rem'}}>
                    <button
                      type="button"
                      onClick={() => setCurrentStep(1)}
                      style={{
                        flex: 1,
                        padding: '1rem',
                        background: '#e5e7eb',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        cursor: 'pointer'
                      }}
                    >
                      ← Back
                    </button>
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      style={{
                        flex: 2,
                        padding: '1rem',
                        background: isSubmitting ? '#9ca3af' : 'linear-gradient(135deg, #6d28d9, #8b5cf6)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontSize: '1.125rem',
                        fontWeight: '700',
                        cursor: isSubmitting ? 'not-allowed' : 'pointer'
                      }}
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit Request ✓'}
                    </button>
                  </div>
                </div>
              )}
            </form>
          </div>

          {/* Trust Indicators */}
          <div style={{display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '1.5rem', marginTop: '3rem'}}>
            <div style={{textAlign: 'center', padding: '1.5rem', background: 'white', borderRadius: '12px'}}>
              <div style={{fontSize: '2rem', marginBottom: '0.5rem'}}>🔒</div>
              <div style={{fontSize: '0.875rem', fontWeight: '600', color: '#1f2937'}}>Secure & Encrypted</div>
            </div>
            <div style={{textAlign: 'center', padding: '1.5rem', background: 'white', borderRadius: '12px'}}>
              <div style={{fontSize: '2rem', marginBottom: '0.5rem'}}>✓</div>
              <div style={{fontSize: '0.875rem', fontWeight: '600', color: '#1f2937'}}>TCPA Compliant</div>
            </div>
            <div style={{textAlign: 'center', padding: '1.5rem', background: 'white', borderRadius: '12px'}}>
              <div style={{fontSize: '2rem', marginBottom: '0.5rem'}}>⚡</div>
              <div style={{fontSize: '0.875rem', fontWeight: '600', color: '#1f2937'}}>24hr Response</div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
